// Live notification "card" shown in the top-right corner when the user is logged in and
// online. It is rendered as react-toastify custom content (see Container.js live handler) and
// mirrors the visual language of the notification panel items, but stands alone so it needs no
// panel styles. The durable copy still lands in the panel + backend; this is only the popup.
import PropTypes from "prop-types";
import "./notification-toast.style.scss";

// Category -> accent colour, kept in sync with the panel's np-cat-* chips.
const CATEGORY_ACCENT = {
  approval: "#F79009",
  scan: "#3B82F6",
  system: "#6B7280",
};

const CATEGORY_LABEL = {
  approval: "Approval",
  scan: "Scan",
  system: "System",
};

const CardIcon = ({ accent }) => (
  <span className="ntoast-icon" style={{ borderColor: accent }}>
    <svg width="18" height="18" viewBox="0 0 22 24" fill="none">
      <path
        d="M17 8A6 6 0 1 0 5 8c0 7-3 9-3 9h18s-3-2-3-9ZM12.73 21a2 2 0 0 1-3.46 0"
        stroke={accent}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

CardIcon.propTypes = { accent: PropTypes.string.isRequired };

/**
 * @param {object}   notification  UI notification item (from buildLiveNotification).
 * @param {function} onView        Opens the full notification panel (optional).
 */
const NotificationToast = ({ notification, onView }) => {
  const accent = CATEGORY_ACCENT[notification.category] || "#3B82F6";
  const categoryLabel = CATEGORY_LABEL[notification.category] || "General";

  return (
    <div
      className="ntoast-card"
      style={{ borderLeftColor: accent }}
      role="button"
      tabIndex={0}
      onClick={onView}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          onView?.();
        }
      }}
    >
      <CardIcon accent={accent} />
      <div className="ntoast-body">
        <div className="ntoast-top">
          <span className="ntoast-title">{notification.title}</span>
          <span
            className="ntoast-category"
            style={{ color: accent, borderColor: accent }}
          >
            {categoryLabel}
          </span>
        </div>
        <p className="ntoast-message">{notification.message}</p>
        {notification.time && (
          <span className="ntoast-time">{notification.time}</span>
        )}
      </div>
    </div>
  );
};

NotificationToast.propTypes = {
  notification: PropTypes.shape({
    title: PropTypes.string,
    message: PropTypes.string,
    category: PropTypes.string,
    time: PropTypes.string,
  }).isRequired,
  onView: PropTypes.func,
};

export default NotificationToast;
